/**
 * Scroll-lock guard — keeps the page where the visitor left it when a
 * third-party popup locks scrolling.
 *
 * Why: the Alia popup (app embed) opens on a scroll-depth trigger near the
 * footer. To lock scrolling it sets an inline `overflow: hidden` on <body> and
 * calls `.focus()` on its full-screen `position: fixed` root without
 * `preventScroll`. iOS Safari / in-app browsers respond by resetting the
 * document scroll to the top, so the page "jumps" up and the sticky header
 * reappears. Other popup apps (Klaviyo etc.) use the same lock pattern.
 *
 * How: when <body>/<html> gains an inline `overflow: hidden`, remember the
 * scroll position. While locked, and right after unlocking, put the page back
 * at that position if something reset it.
 *
 * The theme's own dialogs lock with `body { position: fixed }` and restore the
 * position themselves (dialog.js), so those locks are ignored here.
 */
(function () {
  'use strict';

  if (window.__titanScrollLockGuard) return;
  window.__titanScrollLockGuard = true;

  var TOLERANCE = 50; // px of drift we leave alone

  var body = document.body;
  var html = document.documentElement;
  var lastY = window.scrollY;
  var savedY = null;

  function isThirdPartyLock() {
    if (body.style.position === 'fixed') return false; // theme dialog.js lock
    return body.style.overflow === 'hidden' || html.style.overflow === 'hidden';
  }

  function restore() {
    if (savedY === null) return;
    if (Math.abs(window.scrollY - savedY) > TOLERANCE) {
      // `html { scroll-behavior: smooth }` would otherwise animate this.
      window.scrollTo({ top: savedY, behavior: 'instant' });
    }
  }

  function onLockChange() {
    var locked = isThirdPartyLock();

    if (locked && savedY === null) {
      // Use the last position seen before the lock in case the reset already
      // happened in the same task as the style change.
      savedY = Math.abs(window.scrollY - lastY) > TOLERANCE ? lastY : window.scrollY;
      requestAnimationFrame(restore);
    } else if (!locked && savedY !== null) {
      // Restore on the next frames: some scripts reset scroll right after
      // removing the lock (or when moving focus back to the page).
      requestAnimationFrame(function () {
        restore();
        requestAnimationFrame(function () {
          restore();
          savedY = null;
        });
      });
    }
  }

  window.addEventListener(
    'scroll',
    function () {
      if (savedY !== null) {
        restore();
        return;
      }
      lastY = window.scrollY;
    },
    { passive: true }
  );

  var observer = new MutationObserver(onLockChange);
  observer.observe(body, { attributes: true, attributeFilter: ['style'] });
  observer.observe(html, { attributes: true, attributeFilter: ['style'] });
})();
